import MoreVertIcon from "@mui/icons-material/MoreVert";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Box, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";

import { useNotes } from "api/services/useNotes";
import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { CollapsibleText } from "new-components/collapsibleText/CollapsibleText";
import { Modal } from "new-components/modals/Modal";
import { LocalNote, LocalNotes, Note, NoteOwner } from "types/notes";
import { generateImageUrl } from "utils/generateImageUrl";
import { convertUTCtoLocalReadableDate, createImageURLFromBinary } from "utils/utilityFunctions";

import { CustomEditorNew } from "../CustomEditor/CustomEditorNew";
import { ImageModal } from "../CustomEditor/ImageModal/ImageModal";
import { EditableImageCard } from "../EditableImageCard/EditableImageCard";

interface NoteItemProps {
  data: Note | LocalNote;
  ownerNote: NoteOwner;
  isOwner: boolean;
  canEditGLNotes: boolean;
  canModify: boolean;
  showSortableDrag: boolean;
  isLocalModeActive?: boolean;
  setNotesData?: React.Dispatch<React.SetStateAction<LocalNotes>>;
  lastReview?: string;
  sortableDrag: () => React.JSX.Element;
}

export function NoteItem(props: NoteItemProps) {
  const {
    data,
    ownerNote,
    canModify,
    showSortableDrag,
    isLocalModeActive,
    setNotesData,
    isOwner,
    canEditGLNotes,
    lastReview,
    sortableDrag,
  } = props;
  const [isReviewed, setIsReviewed] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editType, setEditType] = useState<"image" | "text" | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [openModalImage, setOpenModalImage] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [menuAnchorElement, setMenuAnchorElement] = React.useState<null | HTMLElement>(null);

  const { updateNote, isUpdatingNote, deleteNote, deleteNoteImage, isLoading } = useNotes();

  const onClose = () => {
    setOpenModalImage(false);
  };

  const handleModifyImage = (file: File) => {
    setImageFile(file);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>, type: "image" | "text") => {
    setEditType(type);
    if (setMenuAnchorElement) {
      setMenuAnchorElement(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setMenuAnchorElement(null);
  };

  const onEditNote = () => {
    setIsEdit(true);
    handleCloseMenu();
  };

  const handleOpenModal = () => {
    setDeleteModalOpen(true);
    handleCloseMenu();
  };

  const onDeleteNote = async () => {
    handleCloseMenu();
    setDeleteModalOpen(false);
    if (editType === "image") {
      deleteNoteImage({ id: data.id });
    } else {
      deleteNote({ id: data.id });
    }
  };

  const updateForImageEdit = (dataArray: LocalNote[], data: LocalNote) =>
    dataArray.map((item) => (item.id === data.id ? { ...item, image: null } : item));

  const removeNoteById = (dataArray: LocalNote[], id: string) => dataArray.filter((item) => item.id !== id);

  const updateNotesData = (prevNotes: LocalNotes, editType: "image" | "text" | null, data: LocalNote | Note) => {
    const isImageEdit = editType === "image";
    const updatedArray = isImageEdit
      ? updateForImageEdit(prevNotes[ownerNote], data)
      : removeNoteById(prevNotes[ownerNote], data.id);

    return {
      ...prevNotes,
      [ownerNote]: updatedArray,
    };
  };

  const onDeleteLocalNote = () => {
    handleCloseMenu();
    setDeleteModalOpen(false);

    if (setNotesData) {
      setNotesData((prevNotes) => updateNotesData(prevNotes, editType, data));
    }
  };

  const onChangeImage = useCallback(async () => {
    if (!imageFile) {
      return;
    }
    updateNote({
      id: data.id,
      image: imageFile,
    });
    setIsEdit(false);
  }, [imageFile, updateNote, data.id]);

  const updateNotesWithImage = (prevNotes: LocalNotes, noteId: string, imageFile: File | null) => {
    const updatedArray = prevNotes[ownerNote].map((item) =>
      item.id === noteId ? { ...item, image: imageFile as any } : item
    );

    return {
      ...prevNotes,
      [ownerNote]: updatedArray,
    };
  };

  const onChangeLocalNoteImage = () => {
    if (!imageFile) {
      return;
    }

    if (setNotesData) {
      setNotesData((prevNotes) => updateNotesWithImage(prevNotes, data.id, imageFile));
      setIsEdit(false);
    }
  };

  const onChangeNote = async (note: Record<string, any>) => {
    updateNote({
      id: data.id,
      ...note,
    });
    if (isOwner && ownerNote === NoteOwner.GL)
      enqueueSnackbar("The edited note has been moved to “My notes” section.", { variant: "success" });
    setIsEdit(false);
  };

  const onChangeLocalNote = (note: Record<string, any>) => {
    try {
      const newNote: Record<string, any> = { content: note.content };
      if (note.image) {
        newNote.image = note.image;
      }

      if (setNotesData) {
        setNotesData((prevNotes) => {
          const modifiedArray = prevNotes[ownerNote].map((item) => {
            if (item.id === data.id) {
              return {
                ...item,
                ...newNote,
              };
            }

            return item;
          });
          return {
            ...prevNotes,
            [ownerNote]: modifiedArray,
          };
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsEdit(false);
    }
  };

  const onChangePinnedNote = async (note: Record<string, any>) => {
    updateNote({
      id: data.id,
      is_pinned: !note.is_pinned,
    });
  };

  const onChangeLocalPinnedNote = (note: Record<string, any>) => {
    try {
      const newNote: Record<string, any> = { is_pinned: !note.is_pinned };
      if (setNotesData) {
        setNotesData((prevNotes) => {
          const modifiedArray = prevNotes[ownerNote].map((item) => {
            if (item.id === data.id) {
              return {
                ...item,
                ...newNote,
              };
            }
            return item;
          });
          return {
            ...prevNotes,
            [ownerNote]: modifiedArray,
          };
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!lastReview || !data.updated_at) {
      setIsReviewed(true);
    } else {
      const lastReviewDate = new Date(lastReview);
      const updatedDate = new Date(data.updated_at);
      if (lastReviewDate < updatedDate) {
        setIsReviewed(true);
      }
    }
  }, [lastReview, data]);

  useEffect(() => {
    (async () => {
      if (editType === "image") {
        if (isLocalModeActive) {
          onChangeLocalNoteImage();
        } else {
          await onChangeImage();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editType, onChangeImage, imageFile]);

  const imageUrlFromData = data.image_id ? generateImageUrl(undefined, data.image_id) : undefined;
  const imageUrl = isLocalModeActive ? createImageURLFromBinary(data.image as any) : imageUrlFromData;

  const deleteModal = (
    <Modal
      open={deleteModalOpen}
      title={editType === "image" ? "Delete image note" : "Delete note"}
      titleAlignment="left"
      size="small"
      confirmationButtonTitle="Accept"
      cancelButtonTitle="Cancel"
      body={
        <Typography fontSize={18} color="#15121A" sx={{ textAlign: "center" }}>
          {editType === "image"
            ? "Are you sure you want to permanently delete this image? Once deleted, it cannot be recovered."
            : "Are you sure you want to permanently delete this note? Once deleted, it cannot be recovered."}
        </Typography>
      }
      onClose={() => setDeleteModalOpen(false)}
      onCancel={() => setDeleteModalOpen(false)}
      onConfirmation={isLocalModeActive ? onDeleteLocalNote : onDeleteNote}
    />
  );

  if (isEdit)
    return (
      <>
        <CustomEditorNew
          defaultValueText={data.content}
          onSaveNote={isLocalModeActive ? onChangeLocalNote : onChangeNote}
          isLoading={isLoading}
          isEdit={isEdit}
          onEditCancel={() => setIsEdit(false)}
          imageUrl={imageUrl}
          isNotePinned={data.is_pinned}
          onDeleteBackendImage={
            data.image_id
              ? () => {
                  setEditType("image");
                  setDeleteModalOpen(true);
                }
              : undefined
          }
        />
        {deleteModal}
      </>
    );

  return (
    <>
      {isLoading ? (
        SkeletonIndicator({ height: 190, loading: true })
      ) : (
        <Box className={`flex items-center gap-2.5 ${isReviewed ? "bg-transparent" : "bg-[#c8daecff]"}`}>
          {canModify && showSortableDrag ? sortableDrag() : null}
          <Box className="flex gap-5 w-full bg-white min-h-[190px] p-[10px] rounded-[10px] relative">
            {imageUrl && (
              <EditableImageCard
                sx={{
                  height: "190px",
                }}
                imageUrl={imageUrl}
                canModify={canModify}
              />
            )}

            <Stack className={`w-full ${data.image_id && "py-1.5"}`}>
              <Box className="flex items-center justify-between">
                <Typography variant="title" color="#0450BA">
                  Created: {convertUTCtoLocalReadableDate(data.updated_at)}
                </Typography>

                <Box className="flex gap-2 items-center">
                  {(isOwner || (canEditGLNotes && ownerNote === NoteOwner.GL)) && (
                    <>
                      <IconButton
                        className="h-5 w-5"
                        onClick={() => (isLocalModeActive ? onChangeLocalPinnedNote(data) : onChangePinnedNote(data))}
                        disabled={!canModify || isUpdatingNote}
                      >
                        {data.is_pinned ? (
                          <PushPinIcon className="text-primary-bright" />
                        ) : (
                          <PushPinOutlinedIcon className="text-primary-light" />
                        )}
                      </IconButton>

                      {canModify && (
                        <Box>
                          <IconButton className="h-5 w-5" onClick={(e) => handleMenu(e, "text")}>
                            <MoreVertIcon className="text-black" />
                          </IconButton>
                          <Menu
                            id="menu-appbar"
                            anchorEl={menuAnchorElement}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                            keepMounted
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            open={Boolean(menuAnchorElement)}
                            onClose={handleCloseMenu}
                            MenuListProps={{
                              sx: {
                                padding: 0,
                              },
                            }}
                            slotProps={{
                              paper: {
                                sx: {
                                  borderRadius: "5px",
                                  boxShadow: "0px 4px 4px 0px #00000040",
                                },
                              },
                            }}
                          >
                            <MenuItem onClick={onEditNote}>Edit note</MenuItem>

                            <MenuItem onClick={handleOpenModal}>Delete</MenuItem>
                          </Menu>
                        </Box>
                      )}
                    </>
                  )}
                  {((!isOwner && canEditGLNotes && ownerNote === NoteOwner.CUSTOMER) ||
                    (!isOwner && !canEditGLNotes)) &&
                    data.is_pinned && (
                      <IconButton className="h-5 w-5" disabled>
                        <PushPinIcon className="text-neutral-medium" />
                      </IconButton>
                    )}
                </Box>
              </Box>

              <div data-color-mode="light">
                <CollapsibleText maxHeight={120}>
                  <Box
                    sx={{
                      // force the below MDEditor markdown to have the correct styles
                      "& .wmde-markdown p": {
                        fontSize: "18px",
                      },
                      "& .wmde-markdown p del": {
                        textDecoration: "line-through",
                      },
                    }}
                  >
                    <MDEditor.Markdown source={data.content} style={{ whiteSpace: "pre-wrap" }} />
                  </Box>
                </CollapsibleText>
              </div>
            </Stack>
          </Box>
        </Box>
      )}

      <ImageModal open={openModalImage} onClose={onClose} addImage={handleModifyImage} />

      {deleteModal}
    </>
  );
}
